/**
 * Create Webhook Modal (September 2025)
 *
 * Modal component for creating new webhook endpoints with TanStack Form validation.
 *
 * Features:
 * - Field-level validation with TanStack Form
 * - Event selection with checkboxes and search filter
 * - Terminal-themed styling with Tailwind CSS
 * - Secret display modal after successful creation
 * - SSRF validation and HTTPS enforcement
 */

import { useState, useMemo } from 'react';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type {
  WebhookEventType,
  CreateWebhookInput,
  CreateWebhookResponse,
} from '@keypalace/shared-types';

interface CreateWebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (webhookId: string, secret: string) => void;
}

const WEBHOOK_EVENTS: WebhookEventType[] = [
  'service_key.rotated',
  'service_key.created',
  'child_key.created',
  'child_key.revoked',
  'credits.low_threshold',
  'credits.exhausted',
];

const EVENT_LABELS: Record<WebhookEventType, string> = {
  'service_key.rotated': 'Service Key Rotated',
  'service_key.created': 'Service Key Created',
  'child_key.created': 'Child Key Created',
  'child_key.revoked': 'Child Key Revoked',
  'credits.low_threshold': 'Credits Low Threshold',
  'credits.exhausted': 'Credits Exhausted',
};

const EVENT_DESCRIPTIONS: Record<WebhookEventType, string> = {
  'service_key.rotated': 'Triggered when your service API key is rotated for security',
  'service_key.created': 'Triggered when a new service API key is generated',
  'child_key.created': 'Triggered when a child API key is created for an end user',
  'child_key.revoked': 'Triggered when a child API key is revoked',
  'credits.low_threshold': 'Triggered when credits drop below a configured threshold',
  'credits.exhausted': 'Triggered when credits reach zero',
};

export function CreateWebhookModal({ isOpen, onClose, onSuccess }: CreateWebhookModalProps) {
  const convex = useConvex();
  const queryClient = useQueryClient();
  const [eventSearch, setEventSearch] = useState('');
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [createdWebhookId, setCreatedWebhookId] = useState<string | null>(null);

  const createWebhookMutation = useMutation({
    mutationFn: async (input: CreateWebhookInput) => {
      return await convex.mutation(api.webhooks.mutations.createWebhook, input);
    },
    onSuccess: (response: CreateWebhookResponse) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setCreatedWebhookId(response.webhookId);
      setCreatedSecret(response.secret);
      if (onSuccess) {
        onSuccess(response.webhookId, response.secret);
      }
    },
  });

  const form = useForm({
    defaultValues: {
      name: '',
      url: '',
      events: [] as WebhookEventType[],
      description: '',
      customHeaders: undefined as Record<string, string> | undefined,
      retryConfig: undefined,
    },
    onSubmit: async ({ value }) => {
      try {
        await createWebhookMutation.mutateAsync(value);
      } catch (error) {
        console.error('Failed to create webhook:', error);
      }
    },
  });

  const filteredEvents = useMemo(() => {
    if (!eventSearch.trim()) return WEBHOOK_EVENTS;

    const search = eventSearch.toLowerCase();
    return WEBHOOK_EVENTS.filter((event) => {
      const label = EVENT_LABELS[event].toLowerCase();
      const description = EVENT_DESCRIPTIONS[event].toLowerCase();
      return label.includes(search) || description.includes(search);
    });
  }, [eventSearch]);

  const handleClose = () => {
    form.reset();
    setEventSearch('');
    setCreatedSecret(null);
    setCreatedWebhookId(null);
    onClose();
  };

  const handleCopySecret = async () => {
    if (createdSecret !== null) {
      await navigator.clipboard.writeText(createdSecret);
    }
  };

  if (!isOpen) return null;

  if (createdSecret !== null && createdWebhookId !== null) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
        <div className="terminal-box max-w-2xl w-full mx-4 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-terminal-orange terminal-glow mb-2">
              [WEBHOOK CREATED SUCCESSFULLY]
            </h2>
            <p className="text-terminal-cyan text-sm">
              Save your webhook signing secret - it will only be shown once!
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-terminal-cyan text-sm font-bold mb-2">
              WEBHOOK SECRET (COPY THIS NOW)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={createdSecret}
                readOnly
                className="flex-1 bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 font-mono text-sm focus:outline-none focus:border-terminal-orange"
              />
              <button
                onClick={handleCopySecret}
                className="bg-terminal-cyan text-terminal-black font-bold px-6 border-2 border-terminal-cyan hover:bg-transparent hover:text-terminal-cyan transition-all duration-300"
              >
                COPY
              </button>
            </div>
            <p className="text-terminal-cyan/70 text-xs mt-2">
              {createdSecret.startsWith('test_wh_') ? '⚠️ Test secret (development)' : '🔒 Production secret'}
            </p>
          </div>

          <div className="bg-terminal-black border-2 border-terminal-cyan p-4 mb-6">
            <h3 className="text-terminal-orange font-bold mb-2">[IMPORTANT]</h3>
            <ul className="text-terminal-cyan text-sm space-y-1 list-disc list-inside">
              <li>Store this secret securely in your application</li>
              <li>Use it to verify webhook signatures (HMAC-SHA256)</li>
              <li>You cannot retrieve this secret later</li>
              <li>Rotate the secret if compromised</li>
            </ul>
          </div>

          <button
            onClick={handleClose}
            className="w-full bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow"
          >
            DONE - I'VE SAVED MY SECRET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
      <div className="terminal-box max-w-4xl w-full mx-4 p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-terminal-orange terminal-glow">
            [CREATE WEBHOOK]
          </h2>
          <button
            onClick={handleClose}
            className="text-terminal-cyan hover:text-terminal-orange text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-6"
        >
          <form.Field
            name="name"
            validators={{
              onChange: ({ value }) => {
                if (!value || value.length < 3) {
                  return 'Webhook name must be at least 3 characters';
                }
                if (value.length > 100) {
                  return 'Webhook name must not exceed 100 characters';
                }
                return undefined;
              },
              onSubmit: ({ value }) => {
                if (!value || value.length < 3) {
                  return 'Webhook name must be at least 3 characters';
                }
                if (value.length > 100) {
                  return 'Webhook name must not exceed 100 characters';
                }
                return undefined;
              },
            }}
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  WEBHOOK NAME *
                </label>
                <input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Production API Webhook"
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-white text-xs mt-1 border-2 border-red-500 bg-red-500/10 p-2 rounded">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="url"
            validators={{
              onChange: ({ value }) => {
                if (!value) {
                  return 'Webhook URL is required';
                }
                try {
                  const url = new URL(value);
                  if (url.protocol !== 'https:') {
                    return 'Webhook URL must use HTTPS protocol';
                  }
                  const hostname = url.hostname.toLowerCase();
                  if (
                    hostname === 'localhost' ||
                    hostname.startsWith('127.') ||
                    hostname.startsWith('10.') ||
                    hostname.startsWith('172.16.') ||
                    hostname.startsWith('192.168.') ||
                    hostname.startsWith('169.254.')
                  ) {
                    return 'Webhook URL blocked: potential SSRF attack detected';
                  }
                } catch {
                  return 'Invalid webhook URL format';
                }
                return undefined;
              },
              onSubmit: ({ value }) => {
                if (!value) {
                  return 'Webhook URL is required';
                }
                try {
                  const url = new URL(value);
                  if (url.protocol !== 'https:') {
                    return 'Webhook URL must use HTTPS protocol';
                  }
                  const hostname = url.hostname.toLowerCase();
                  if (
                    hostname === 'localhost' ||
                    hostname.startsWith('127.') ||
                    hostname.startsWith('10.') ||
                    hostname.startsWith('172.16.') ||
                    hostname.startsWith('192.168.') ||
                    hostname.startsWith('169.254.')
                  ) {
                    return 'Webhook URL blocked: potential SSRF attack detected';
                  }
                } catch {
                  return 'Invalid webhook URL format';
                }
                return undefined;
              },
            }}
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  WEBHOOK URL *
                </label>
                <input
                  type="url"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="https://api.example.com/webhooks/keypalace"
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 font-mono focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-white text-xs mt-1 border-2 border-red-500 bg-red-500/10 p-2 rounded">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="events"
            validators={{
              onChange: ({ value }) => {
                if (value.length === 0) {
                  return 'At least one event type must be selected';
                }
                return undefined;
              },
              onSubmit: ({ value }) => {
                if (value.length === 0) {
                  return 'At least one event type must be selected';
                }
                return undefined;
              },
            }}
          >
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  EVENT TYPES * ({field.state.value.length} selected)
                </label>

                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder="Search events..."
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 mb-4 focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30"
                />

                <div className="bg-terminal-black border-2 border-terminal-cyan p-4 max-h-64 overflow-y-auto space-y-3">
                  {filteredEvents.length === 0 ? (
                    <p className="text-terminal-cyan/50 text-sm">No events match your search</p>
                  ) : (
                    filteredEvents.map((event) => (
                      <label
                        key={event}
                        className="flex items-start gap-3 cursor-pointer hover:bg-terminal-cyan/5 p-2 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={field.state.value.includes(event)}
                          onChange={(e) => {
                            const currentEvents = field.state.value;
                            if (e.target.checked) {
                              field.handleChange([...currentEvents, event]);
                            } else {
                              field.handleChange(
                                currentEvents.filter((ev) => ev !== event)
                              );
                            }
                          }}
                          className="mt-1 w-4 h-4 accent-terminal-orange"
                        />
                        <div className="flex-1">
                          <div className="text-terminal-orange font-semibold text-sm">
                            {EVENT_LABELS[event]}
                          </div>
                          <div className="text-terminal-cyan/70 text-xs">
                            {EVENT_DESCRIPTIONS[event]}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {field.state.meta.errors.length > 0 && (
                  <p className="text-white text-xs mt-1 border-2 border-red-500 bg-red-500/10 p-2 rounded">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div>
                <label className="block text-terminal-cyan text-sm font-bold mb-2">
                  DESCRIPTION (Optional)
                </label>
                <textarea
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Additional details about this webhook..."
                  rows={3}
                  className="w-full bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 focus:outline-none focus:border-terminal-orange placeholder-terminal-cyan/30 resize-none"
                />
              </div>
            )}
          </form.Field>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 bg-transparent text-terminal-cyan font-bold py-3 px-6 border-2 border-terminal-cyan hover:bg-terminal-cyan hover:text-terminal-black transition-all duration-300"
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={createWebhookMutation.isPending}
              className="flex-1 bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createWebhookMutation.isPending ? 'CREATING...' : 'CREATE WEBHOOK'}
            </button>
          </div>

          {createWebhookMutation.isError && (
            <div className="bg-red-500/10 border-2 border-red-500 p-4 rounded">
              <p className="text-white text-sm font-bold">
                [ERROR] Failed to create webhook
              </p>
              <p className="text-white/70 text-xs mt-1">
                {(createWebhookMutation.error as Error)?.message || 'Unknown error occurred'}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
